/**
 * network-probe.js — latency + bandwidth probe for cloud-gaming readiness checks.
 *
 * Same-origin probes only. We do NOT cross-origin ping cloud-gaming DC IPs from
 * the browser (CORS-blocked, plus measuring third-party RTT cleanly is unreliable
 * — DNS lookups, TCP setup, and CDN front-doors all dominate the small data
 * point you'd want for "is the controller-to-server lag survivable?").
 *
 * Instead the probe measures:
 *   1. RTT to our origin (50× tiny same-origin GETs, percentiles).
 *      Treats this as a *floor* — real cloud-gaming RTT is usually higher.
 *   2. Bandwidth by moving real bytes off /speedtest/*.bin with the ramp
 *      excluded and the spend bounded (see `_bandwidth` — read that comment
 *      before touching it; the naive version under-reported by 10-50×).
 *   3. navigator.connection.{effectiveType, rtt, downlink, saveData} where present.
 *      ⚠ These are fingerprint-defensive estimates, NOT measurements:
 *      Chromium rounds `downlink` and CAPS IT AT 10 Mbps, and `effectiveType`
 *      has four buckets whose top one ('4g') covers everything from a decent
 *      LTE phone to gigabit fibre. Verified on loopback 2026-08-13: a link
 *      doing 400+ Mbps reports {effectiveType:'4g', downlink:10}. Never print
 *      either one as "your download speed" — bug report #1352 was exactly that.
 *
 * Cloud-gaming recommendation logic is in the consumer tool, not here. This
 * module reports facts. Consumer interprets — e.g. tool maps RTT<30ms +
 * downlink>15Mbps → "ready for GeForce Now Ultimate", 30-50ms → "playable but
 * input feel will degrade", >50ms → "consider native install".
 *
 * Public API (window.NetworkProbe):
 *   .latency({samples=30, signal})  → Promise<{p50, p95, p99, samples, raw[]}>   ms
 *   .bandwidth({durMs, capBytes, streams, rampMs, allowMetered, signal})
 *                                   → Promise<{mbps, floor, bytes, ms, streams, source}>
 *   .throughput({...})              → same measurement, legacy result shape
 *   .connection()                   → {effectiveType, rtt, downlink, saveData} | {}
 *   .full({signal, onProgress})     → Promise<combined snapshot>
 */
(function(){
    'use strict';

    // Bail only if a non-stub instance was already installed (true double-load).
    // The inline-stub installed in app.blade.php sets `__isStub: true` so the
    // real impl can override it cleanly. Without this, the stub permanently
    // shadows the real module and `.full()` rejects with "not loaded".
    if (window.NetworkProbe && !window.NetworkProbe.__isStub) return;

    function _now() { return (performance && performance.now) ? performance.now() : Date.now(); }

    function _percentile(arr, p) {
        if (!arr.length) return 0;
        var sorted = arr.slice().sort(function(a, b){ return a - b; });
        var idx = Math.min(sorted.length - 1, Math.floor(sorted.length * p));
        return sorted[idx];
    }

    function _connection() {
        var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (!c) return {};
        return {
            effectiveType: c.effectiveType || '',
            rtt:           c.rtt || 0,           // ms (rounded by browser)
            downlink:      c.downlink || 0,      // Mbps (rounded)
            saveData:      !!c.saveData
        };
    }

    function _ping(signal) {
        // Use a 1-byte HEAD against /healthz with cache-bust. /healthz is the
        // app health endpoint (small JSON, no PageCache middleware).
        var url = '/healthz?_t=' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
        var t0 = _now();
        return fetch(url, { method:'HEAD', cache:'no-store', signal: signal }).then(function() {
            return _now() - t0;
        });
    }

    function _latency(opts) {
        opts = opts || {};
        var samples = Math.max(5, Math.min(60, opts.samples || 30));
        var signal = opts.signal;
        var raw = [];
        var i = 0;

        function next() {
            if (signal && signal.aborted) { var ae = new Error('aborted'); ae.name = 'AbortError'; return Promise.reject(ae); }
            if (i >= samples) {
                return Promise.resolve({
                    p50: Math.round(_percentile(raw, 0.50)),
                    p95: Math.round(_percentile(raw, 0.95)),
                    p99: Math.round(_percentile(raw, 0.99)),
                    samples: raw.length,
                    raw: raw
                });
            }
            i++;
            return _ping(signal).then(function(ms) {
                raw.push(ms);
                if (typeof opts.onSample === 'function') opts.onSample(ms, i, samples);
            }, function(e) {
                if (e && e.name === 'AbortError') throw e;
                // Single-sample failure — record as outlier (1000ms) and continue
                raw.push(1000);
            }).then(next);
        }

        return next();
    }

    /* ------------------------------------------------------------------
     * Bandwidth.
     *
     * WHAT NOT TO DO — this function used to fetch /api/sidebar/en (~300 KB of
     * Laravel-rendered JSON) once and divide bytes by wall time. Measured
     * 2026-08-13: it reported 5.8 Mbps on a 60 Mbps line and 8.2 Mbps on
     * loopback (real: 400+). It was billing PHP render time to the user's line
     * speed and labelling the result `source:'measured'`. /speed-test learned
     * this exact lesson in comment #804 ("under-measured by an order of
     * magnitude") and fixed it inside its own engine; the shared primitive
     * never got the memo, so every other consumer inherited the broken method.
     *
     * Three rules the method now obeys:
     *   1. STATIC BYTES. /speedtest/*.bin is served by nginx off disk (random
     *      content, so no gzip/brotli layer can shrink the transfer and inflate
     *      the result). Anything Laravel renders measures our CPU, not their line.
     *   2. EXCLUDE THE RAMP. TTFB + TCP slow-start dominate a short transfer.
     *      Steady state = bytes moved AFTER `rampMs`, over the time after it.
     *   3. BOUND THE SPEND, THEN SAY SO. You cannot measure 800 Mbps without
     *      moving ~100 MB, and a scan tool has no business spending that. So the
     *      probe stops at a byte cap and reports `floor:true` — the honest claim
     *      is "at least this fast", not a made-up exact figure. Byte cost
     *      self-scales: a slow line hits the DEADLINE first and pays ~0.6 MB;
     *      only a fast line ever reaches the cap.
     *
     * Calibrated 2026-08-13 against CDP-throttled ground truth (defaults):
     *   5 Mbps → 4.7 (94%, 0.6 MB) · 25 → 24.1 (96%, 3.4 MB)
     *   100 → 96.3 (96%, 4 MB, floor) · unthrottled → 367 (4 MB, floor)
     * Guard: tests/playwright/network-probe-bandwidth.mjs.
     *
     * Returns {mbps, floor, bytes, ms, streams, source}. `source`:
     *   'measured'       — real transfer; trust the number (respect `floor`)
     *   'connection-api' — navigator.connection estimate. CAPPED AT 10 Mbps by
     *                      Chromium and rounded; never present it as a speed.
     *   'save-data'      — user asked us not to spend their data
     *   'unavailable'    — could not measure and had nothing honest to fall back
     *                      on. mbps is null. Show "—", never a number.
     */
    var BLOB_URL = '/speedtest/d25m.bin';

    function _apiFallback(source) {
        var c = _connection();
        return { mbps: c.downlink || null, floor: false, bytes: 0, ms: 0, streams: 0,
                 source: c.downlink ? (source || 'connection-api') : 'unavailable' };
    }

    function _bandwidth(opts) {
        opts = opts || {};
        var durMs    = opts.durMs    || 1200;
        var capBytes = opts.capBytes || 4 * 1048576;
        var streams  = opts.streams  || 2;
        var rampMs   = opts.rampMs != null ? opts.rampMs : 300;
        // Below this the sample is too small to divide by anything meaningful
        // (a 2g line moves ~7 KB in the whole window) — say so instead of
        // inventing a number from noise.
        var minBytes = opts.minBytes || 65536;
        var signal   = opts.signal;

        var conn = _connection();
        if (conn.saveData && !opts.allowMetered) return Promise.resolve(_apiFallback('save-data'));
        if (typeof fetch !== 'function') return Promise.resolve(_apiFallback());

        var t0 = _now(), bytes = 0, capped = false, gotAnyResponse = false, missing = false;
        var marks = [];
        var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
        var tick = setInterval(function(){ marks.push({ t: _now() - t0, b: bytes }); }, 50);
        var stop = function(){ if (ctrl) { try { ctrl.abort(); } catch(_){} } };
        if (signal) { if (signal.aborted) stop(); else { try { signal.addEventListener('abort', stop); } catch(_){} } }

        function worker(i) {
            // Stable per-stream URL: bounded to `streams` distinct objects so a
            // CDN edge can serve them (cheap for the origin) while `no-store`
            // still forces a real network fetch on every round.
            var url = BLOB_URL + '?s=' + i;
            function round() {
                if (missing || bytes >= capBytes || _now() - t0 >= durMs) return Promise.resolve();
                return fetch(url, { cache: 'no-store', signal: ctrl ? ctrl.signal : undefined }).then(function(r) {
                    if (!r.ok) { missing = true; return; }   // blobs absent (dev / fresh origin)
                    gotAnyResponse = true;
                    if (!r.body || typeof r.body.getReader !== 'function') { missing = true; return; }
                    var reader = r.body.getReader();
                    function pump() {
                        return reader.read().then(function(res) {
                            if (res.done) return;
                            bytes += res.value.byteLength;
                            if (bytes >= capBytes) { capped = true; stop(); return; }
                            if (_now() - t0 >= durMs) { stop(); return; }
                            return pump();
                        });
                    }
                    return pump();
                }).then(round);
            }
            return round().catch(function(){ /* abort / drop — bytes already counted really crossed the wire */ });
        }

        var jobs = [];
        for (var i = 0; i < streams; i++) jobs.push(worker(i));

        return Promise.all(jobs).then(function() {
            clearInterval(tick);
            if (signal) { try { signal.removeEventListener('abort', stop); } catch(_){} }
            marks.push({ t: _now() - t0, b: bytes });

            if (!gotAnyResponse || bytes < minBytes) return _apiFallback();

            var end = marks[marks.length - 1], start = null;
            for (var k = 0; k < marks.length; k++) { if (marks[k].t >= rampMs) { start = marks[k]; break; } }
            // Whole transfer finished inside the ramp window — no steady state
            // to speak of; fall back to the whole-window rate rather than
            // dividing by a window that does not exist.
            if (!start || end.t <= start.t) start = { t: 0, b: 0 };
            if (end.t <= start.t) return _apiFallback();

            // A steady-state window too short to be steady. On a fast line the
            // byte cap lands ~85ms after the ramp, and a burst across that
            // sliver reads far above the sustained rate — measured 2026-08-13,
            // 181 Mbps on a line throttled to 100. That number then ships with
            // `floor:true`, i.e. "AT LEAST 181" about a 100 Mbps line, which
            // inverts the one promise a floor makes. When the window is too
            // thin, fall back to the whole-transfer rate: it includes the ramp
            // and therefore UNDER-states, which is the safe direction for a
            // lower bound.
            var minWindowMs = opts.minWindowMs || 250;
            if (end.t - start.t < minWindowMs) start = { t: 0, b: 0 };

            var mbps = ((end.b - start.b) * 8) / ((end.t - start.t) / 1000) / 1e6;
            if (!isFinite(mbps) || mbps <= 0) return _apiFallback();
            return { mbps: Math.round(mbps * 10) / 10, floor: capped, bytes: bytes,
                     ms: Math.round(end.t), streams: streams, source: 'measured' };
        }).catch(function(e) {
            clearInterval(tick);
            if (e && e.name === 'AbortError' && signal && signal.aborted) throw e;
            return _apiFallback();
        });
    }

    /* Back-compat shape for existing consumers ({mbps, bytes, ms, source}).
       Same corrected method underneath — `floor`/`streams` ride along for
       anyone who wants to render "≥" honestly. */
    function _throughput(opts) {
        return _bandwidth(opts).then(function(r) {
            return { mbps: r.mbps || 0, bytes: r.bytes, ms: r.ms,
                     source: r.source, floor: r.floor, streams: r.streams };
        });
    }

    function _stun(opts) {
        // STUN ICE-gathering RTT — UDP-path baseline, much closer to what
        // real cloud-gaming traffic uses than HTTP RTT. Delegates to
        // `__WebRTC.measureStunRtt()` from public/js/webrtc-config.js.
        opts = opts || {};
        if (!window.__WebRTC || typeof window.__WebRTC.measureStunRtt !== 'function') {
            return Promise.resolve({ rttMs: -1, source: 'no-webrtc-config' });
        }
        // Without an explicit server measureStunRtt asks this page's own STUN
        // (Beget for /ru/, NL for the other locales — webrtc-config.js).
        return window.__WebRTC.measureStunRtt({
            stun:    opts.stun,
            timeout: opts.timeout || 4000,
            signal:  opts.signal
        });
    }

    function _full(opts) {
        opts = opts || {};
        var signal = opts.signal;
        var conn = _connection();
        if (typeof opts.onProgress === 'function') opts.onProgress({stage:'latency', pct:5});
        return _latency({
            samples: opts.samples,
            signal:  signal,
            onSample: function(ms, i, total) {
                if (typeof opts.onProgress === 'function') {
                    opts.onProgress({stage:'latency', pct: 5 + Math.round((i / total) * 55)});
                }
            }
        }).then(function(lat) {
            if (typeof opts.onProgress === 'function') opts.onProgress({stage:'throughput', pct:65});
            // Consumers that judge against thresholds (cloud-gaming service
            // minimums run to 35 Mbps) need a bigger byte budget than a passive
            // scan, or a floor short of the threshold reads as a FAIL when the
            // line is actually fine. Pass `bandwidth:{...}` to widen it.
            var bwOpts = {};
            if (opts.bandwidth) for (var k in opts.bandwidth) if (Object.prototype.hasOwnProperty.call(opts.bandwidth, k)) bwOpts[k] = opts.bandwidth[k];
            bwOpts.signal = signal;
            return _throughput(bwOpts).then(function(thr) {
                if (typeof opts.onProgress === 'function') opts.onProgress({stage:'stun', pct:85});
                // STUN RTT runs after throughput — failure-tolerant, never blocks
                // overall completion (4s timeout caps it).
                return _stun({signal: signal}).then(function(stun) {
                    if (typeof opts.onProgress === 'function') opts.onProgress({stage:'done', pct:100});
                    return { latency: lat, throughput: thr, connection: conn, stun: stun };
                }, function() {
                    if (typeof opts.onProgress === 'function') opts.onProgress({stage:'done', pct:100});
                    return { latency: lat, throughput: thr, connection: conn, stun: { rttMs: -1, source: 'error' } };
                });
            });
        });
    }

    var NetworkProbe = {
        latency:    _latency,
        throughput: _throughput,
        bandwidth:  _bandwidth,
        connection: _connection,
        stun:       _stun,
        full:       _full
    };

    Object.defineProperty(NetworkProbe, '__isStub', { value: false });
    window.NetworkProbe = NetworkProbe;
})();
